import React, { useState } from 'react';

/**
 * Step 3A: Batch Selection UI
 * Students select which AI shortcuts to delegate
 */
export const BatchSelectionStep = ({
  response,
  selectedOffers,
  onToggleOffer,
  onStartGeneration
}) => {
  if (!response) return null;

  const offers = response.autoGenRecommendations || [];
  const selectedCount = Object.values(selectedOffers).filter(Boolean).length;

  // Calculate total time saved
  const totalTime = offers.reduce((sum, offer, idx) => {
    return selectedOffers[idx] ? sum + (offer.timeSaved || 0) : sum;
  }, 0);

  const hours = Math.floor(totalTime / 60);
  const mins = totalTime % 60;
  let timeText = '';
  if (hours > 0) timeText += `${hours} hour${hours > 1 ? 's' : ''}`;
  if (hours > 0 && mins > 0) timeText += ' ';
  if (mins > 0) timeText += `${mins} min`;

  const getQualityBadgeClass = (quality) => {
    switch(quality) {
      case 'excellent': return 'bg-teal-600 text-white';
      case 'good': return 'bg-teal-600 text-white';
      default: return 'bg-slate-500 text-white';
    }
  };

  return (
    <div className="bg-stone-50 rounded-xl max-w-4xl w-full p-8 max-h-[90vh] overflow-y-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-xl font-bold text-slate-900 mb-2 flex items-center justify-center gap-3">
          <span className="text-xl">✨</span>
          Your Rescue Plan
        </h1>
        <p className="text-slate-600 text-md">Choose what to delegate to AI so you can focus on what matters most to you</p>
      </div>

      {/* Main Delegation Card */}
      <div className="bg-white rounded-xl border-2 border-slate-900 shadow-sm p-8 mb-6">
        <div className="mb-6">
          <h2 className="text-lg font-bold text-slate-900 mb-2">AI Task List</h2>
        </div>

        {/* Summary Bar */}
        <div className="bg-teal-600 border-2 border-slate-900 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-white">{selectedCount} of {offers.length} tasks selected</p>
              <p className="text-xs text-white">Total time you'll save: ~{timeText || '0 min'}</p>
            </div>
            {selectedCount === offers.length && (
              <div className="bg-teal-50 text-teal-900 text-xs px-3 py-1.5 rounded-full font-medium">
                ✓ All Recommended
              </div>
            )}
          </div>
        </div>

        {/* Clickable Delegation List */}
        <div className="space-y-2 mb-6">
          {offers.map((offer, idx) => (
            <div
              key={idx}
              onClick={() => onToggleOffer(idx)}
              className={`flex items-start gap-4 p-4 rounded-lg border-2 transition-all cursor-pointer ${
                selectedOffers[idx]
                  ? 'bg-slate-50 border-slate-500 hover:bg-slate-100'
                  : 'bg-stone-50 border-stone-300 opacity-60'
              }`}
            >
              <div className="flex items-center pt-0.5">
                <input
                  type="checkbox"
                  checked={selectedOffers[idx] || false}
                  onChange={() => {}}
                  className="w-5 h-5 text-teal-600 rounded cursor-pointer pointer-events-none"
                />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-base font-bold text-slate-900">
                    {offer.displayName || offer.taskTitle}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getQualityBadgeClass(offer.quality)}`}>
                    {offer.quality === 'excellent' ? 'Excellent' : offer.quality === 'good' ? 'Good' : 'Basic'}
                  </span>
                </div>
                <p className="text-sm text-slate-700 mb-2">{offer.description}</p>
                <div className="flex items-center gap-4 text-xs text-slate-600">
                  <span>⏱ Saves ~{offer.timeSaved} min</span>
                  {offer.taskCount && <span>📚 {offer.taskCount} tasks</span>}
                  {offer.collegeName && <span>🎯 {offer.collegeName}</span>}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Big Delegation Button */}
        <button
          onClick={onStartGeneration}
          disabled={selectedCount === 0}
          className={`w-full font-bold py-4 px-6 rounded-lg text-lg transition-all border-2 border-slate-900 shadow-sm ${
            selectedCount === 0
              ? 'bg-stone-200 text-stone-500 cursor-not-allowed'
              : 'bg-slate-900 hover:bg-slate-800 text-white'
          }`}
        >
          {selectedCount === 0 ? '← Select at least one task to delegate' : `Delegate ${selectedCount} Task${selectedCount !== 1 ? 's' : ''} to AI`}
        </button>

        <p className="text-xs text-center text-slate-500 mt-3">
          Takes 2-3 minutes · You can work in other tabs · We'll notify you when done
        </p>
      </div>

      {/* Already Handled Section */}
      {(response.priorityCount || response.deferredCount || response.autoGenRecommendations) && (
        <div className="bg-slate-100 border-2 border-slate-300 rounded-xl p-6 shadow-sm">
          <div className="flex items-start gap-4 mb-5">
            <div className="w-12 h-12 rounded-xl bg-slate-700 text-white flex items-center justify-center flex-shrink-0">
              <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-slate-900 mb-1">Already Handled</h3>
              <p className="text-sm text-slate-600">While you were reading this page</p>
            </div>
          </div>

          {/* 2x2 Card Grid */}
          <div className="grid md:grid-cols-2 gap-4">
            {response.priorityCount && (
              <div className="bg-white rounded-lg p-4 border border-slate-200">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-bold text-slate-900 mb-1">Analyzed {response.priorityCount} priority tasks</p>
                    <p className="text-xs text-slate-600">ED, essays, recs, FAFSA</p>
                  </div>
                </div>
              </div>
            )}

            {response.deferredCount > 0 && (
              <div className="bg-white rounded-lg p-4 border border-slate-200">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-bold text-slate-900 mb-1">Rescheduled {response.deferredCount} RD tasks</p>
                    <p className="text-xs text-slate-600">Post-ED deadline (Nov 6-26)</p>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-white rounded-lg p-4 border border-slate-200">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-bold text-slate-900 mb-1">Created 3 priority buckets</p>
                  <p className="text-xs text-slate-600">This week, next week, can wait</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-4 border border-slate-200">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M13 7H7v6h6V7z" />
                    <path fillRule="evenodd" d="M7 2a1 1 0 012 0v1h2V2a1 1 0 112 0v1h2a2 2 0 012 2v2h1a1 1 0 110 2h-1v2h1a1 1 0 110 2h-1v2a2 2 0 01-2 2h-2v1a1 1 0 11-2 0v-1H9v1a1 1 0 11-2 0v-1H5a2 2 0 01-2-2v-2H2a1 1 0 110-2h1V9H2a1 1 0 010-2h1V5a2 2 0 012-2h2V2zM5 5h10v10H5V5z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-bold text-slate-900 mb-1">Found {offers.length} AI shortcuts</p>
                  <p className="text-xs text-slate-600">To save you even more time</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-slate-200 border-l-4 border-slate-600 rounded-r-lg p-3 mt-4">
            <p className="text-sm text-slate-900">
              <strong>You're already ahead.</strong> The delegation above takes you even further.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Step 3B: Productive Generation
 * Shows progress with educational insights while AI generates content
 */
export const GenerationProgressStep = ({
  progress,
  currentInsight,
  educationalInsights,
  completedItems
}) => {
  const progressPercent = progress.total > 0 ? (progress.completed / progress.total) * 100 : 0;
  const remainingTime = (progress.total - progress.completed) * 3;

  const insight = educationalInsights[currentInsight % educationalInsights.length];

  return (
    <div className="bg-white rounded-xl border-2 border-slate-300 p-8 max-w-4xl w-full">
      <h3 className="text-2xl font-bold text-slate-900 mb-6 text-center">✨ Delegating to AI...</h3>

      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-slate-700">
            {progress.completed} of {progress.total} complete
          </span>
          <span className="text-sm text-stone-600">
            {remainingTime > 0 ? `~${remainingTime} sec remaining` : 'Almost done...'}
          </span>
        </div>
        <div className="w-full bg-stone-200 rounded-full h-3 overflow-hidden">
          <div
            className="bg-gradient-to-r from-teal-500 to-emerald-500 h-3 rounded-full transition-all duration-500"
            style={{ width: `${progressPercent}%` }}
          ></div>
        </div>
      </div>

      {/* Current Insight */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-6 mb-6 animate-pulse">
        <h4 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
          <span className="text-xl">💡</span>
          <span>{insight.title}</span>
        </h4>
        <p className="text-slate-700 leading-relaxed">{insight.text}</p>
      </div>

      {/* Completion List */}
      <div className="space-y-2">
        {completedItems.map((item, idx) => (
          <div key={idx} className="flex items-center gap-2 text-sm animate-slide-in">
            <svg className="w-5 h-5 text-emerald-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
            </svg>
            <span className="text-slate-700">{item.displayName || item.taskTitle}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Step 3C: Review & Save Confirmations
 * Shows expandable cards with generated content
 */
export const ReviewStep = ({
  generatedResults,
  onShowAccessMap,
  onContinue
}) => {
  const [expandedCards, setExpandedCards] = useState({});

  const toggleExpand = (cardId) => {
    setExpandedCards(prev => ({
      ...prev,
      [cardId]: !prev[cardId]
    }));
  };

  const totalTimeSaved = generatedResults.reduce((sum, result) => sum + (result.timeSaved || 0), 0);
  const hours = Math.floor(totalTimeSaved / 60);
  const mins = totalTimeSaved % 60;
  let timeText = '~';
  if (hours > 0) timeText += `${hours} hour${hours !== 1 ? 's' : ''} `;
  if (mins > 0) timeText += `${mins} min`;

  return (
    <div className="max-w-4xl w-full space-y-6">
      {/* Calm, Confident Header */}
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-20 h-20 bg-slate-800 rounded-full mb-4 shadow-md">
          <svg className="w-12 h-12 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        </div>
        <h1 className="text-3xl font-bold text-slate-900 mb-2">All Done. Everything's In Place.</h1>
        <p className="text-slate-600 text-lg">{generatedResults.length} tasks completed and saved to your timeline</p>
      </div>

      {/* Main Results Card */}
      <div className="bg-white rounded-xl border-2 border-slate-900 shadow-sm overflow-hidden">
        {/* Calm Success Banner */}
        <div className="bg-slate-800 px-6 py-4 border-b-2 border-slate-900">
          <div className="flex items-center gap-3 text-white">
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="font-bold text-lg">All Content Saved & Ready to Review</span>
          </div>
        </div>

        {/* Results List - Expandable Cards */}
        <div className="p-6 space-y-3">
          {generatedResults.map((result, idx) => {
            const cardId = `expand-${idx}`;
            const isExpanded = expandedCards[cardId];

            return (
              <div key={idx} className="border-2 border-slate-200 bg-white rounded-lg overflow-hidden transition-all hover:border-slate-300 hover:shadow-sm">
                <div className="p-4">
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <div className="flex items-center gap-3 flex-1">
                      <div className="w-8 h-8 rounded-lg bg-slate-700 text-white flex items-center justify-center flex-shrink-0">
                        <div dangerouslySetInnerHTML={{ __html: result.iconSvg || '<svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" /></svg>' }} />
                      </div>
                      <h3 className="text-base font-bold text-slate-900">{result.displayName || result.taskTitle}</h3>
                    </div>
                    {result.expandedDetails && result.expandedDetails.length > 0 && (
                      <button
                        onClick={() => toggleExpand(cardId)}
                        className="text-sm font-medium text-slate-600 hover:text-slate-900 flex items-center gap-1 flex-shrink-0"
                      >
                        <span>{isExpanded ? 'Hide' : 'Expand'}</span>
                        <svg
                          className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    )}
                  </div>

                  <p className="text-sm text-slate-700 mb-3 ml-11">{result.description}</p>

                  {isExpanded && result.expandedDetails && result.expandedDetails.length > 0 && (
                    <div className="bg-stone-50 rounded-lg p-4 mb-3 ml-11 border border-stone-200">
                      <ul className="space-y-2 text-sm text-slate-800">
                        {result.expandedDetails.map((detail, detailIdx) => (
                          <li key={detailIdx} className="flex items-start gap-2">
                            <span className="text-slate-500 mt-0.5">•</span>
                            <span>{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="inline-flex items-center gap-2 bg-slate-100 text-slate-700 text-xs px-3 py-1.5 rounded-md font-medium ml-11 border border-slate-200">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                      <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                    </svg>
                    <span>Saved to: {result.location || 'Timeline'}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Info Banner */}
        <div className="bg-indigo-50 border-t-2 border-indigo-300 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-indigo-500 text-white flex items-center justify-center flex-shrink-0">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1">
              <p className="text-sm font-bold text-indigo-900 mb-1">All content auto-saved to your timeline</p>
              <p className="text-xs text-indigo-800">Look for the <span className="inline-flex items-center gap-1 bg-indigo-100 px-2 py-0.5 rounded font-medium text-indigo-700">🤖 AI Draft</span> badge on tasks</p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="px-6 py-5 bg-stone-50 border-t border-stone-200 flex gap-3">
          <button
            onClick={onShowAccessMap}
            className="flex-1 bg-white text-slate-900 border-2 border-slate-900 font-bold py-3 px-6 rounded-lg hover:bg-stone-50 transition-colors flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Show Me Where to Find These
          </button>
          <button
            onClick={onContinue}
            className="flex-1 bg-slate-800 text-white border-2 border-slate-900 font-bold py-3 px-6 rounded-lg hover:bg-slate-700 transition-colors flex items-center justify-center gap-2"
          >
            Continue to Timeline
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </button>
        </div>
      </div>

      {/* Calm Stats Card */}
      <div className="bg-gradient-to-br from-slate-50 to-stone-100 border-2 border-slate-300 rounded-xl p-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-xl bg-slate-700 text-white flex items-center justify-center flex-shrink-0 shadow-sm">
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-bold text-slate-900 mb-1">{timeText} saved</h3>
            <p className="text-sm text-slate-700">Time you can spend on your essays, test prep, or actually being a high school student</p>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Step 3D: Access Map Overlay (Modal)
 * Shows students where to find generated content
 */
export const AccessMapModal = ({
  isVisible,
  generatedResults,
  onClose,
  onGoToTimeline
}) => {
  if (!isVisible) return null;

  // Group items by location
  const timelineItems = [];
  const collegeHubItems = [];

  generatedResults.forEach(result => {
    const locationKey = result.location || 'Timeline';
    const isTimeline = locationKey.includes('Timeline');

    const item = (
      <div key={result.taskId} className="flex items-start gap-2 text-sm text-slate-700">
        <span className="text-slate-400 mt-0.5">→</span>
        <span><strong>{result.location || 'Timeline'}</strong> - {result.displayName || result.taskTitle}</span>
      </div>
    );

    if (isTimeline) {
      timelineItems.push(item);
    } else {
      collegeHubItems.push(item);
    }
  });

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden border-2 border-slate-900 animate-slide-in">
        {/* Header */}
        <div className="bg-slate-800 px-8 py-6 border-b-2 border-slate-900">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center flex-shrink-0">
              <svg className="w-8 h-8 text-slate-800" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-white mb-2">All Set. Here's Where to Find Everything</h2>
              <p className="text-slate-200 text-sm">All content has been saved and is ready to review. Look for the <span className="inline-flex items-center gap-1 bg-indigo-100 px-2 py-0.5 rounded font-medium text-indigo-700 text-xs">🎨 AI Draft</span> badge.</p>
            </div>
          </div>
        </div>

        {/* Content - Grouped by Location */}
        <div className="p-8 overflow-y-auto max-h-[60vh]">
          {/* Timeline Section */}
          {timelineItems.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-slate-700 text-white flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-slate-900">Timeline</h3>
              </div>
              <div className="ml-13 space-y-2">
                {timelineItems}
              </div>
            </div>
          )}

          {/* College Hub Section */}
          {collegeHubItems.length > 0 && (
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-slate-700 text-white flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-slate-900">College Hub</h3>
              </div>
              <div className="ml-13 space-y-2">
                {collegeHubItems}
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="px-8 py-6 bg-stone-50 border-t border-stone-200 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 bg-white text-slate-700 border-2 border-slate-300 font-semibold py-3 px-6 rounded-lg hover:bg-stone-50 transition-colors"
          >
            I'll Find It Later
          </button>
          <button
            onClick={onGoToTimeline}
            className="flex-1 bg-slate-800 text-white border-2 border-slate-900 font-bold py-3 px-6 rounded-lg hover:bg-slate-700 transition-colors flex items-center justify-center gap-2"
          >
            View My Timeline
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};
